import * as React from "react";
import { useCallback, useEffect } from "react";

import { css } from "@emotion/react";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  DownloadFill,
  LockFill,
  ArrowLeft as ArrowLeftIcon,
  CheckCircle,
} from "@hl/base-components/lib/assets/icons.generated";
import {
  SECONDARY_COLOR,
  OUTLINE_COLOR,
} from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import {
  Anchor,
  AppShell,
  Button,
  Card,
  Container,
  CopyButton,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ReactComponent as BracketsEllipsisIcon } from "assets/icons/brakets-ellipsis.svg";
import { mapImportTokenToBlockchainEntity } from "~/components/BlockchainEntityTitle/types";
import { TOOLS_URL } from "~/config";
import { ModalType, updateModalVar } from "~/features/layout/Modal/modal";
import ManageTokenHeader from "~/features/tokens/sections/ManageToken/ManageTokenHeader";
import { DescriptionProperty } from "~/features/tokens/sections/ManageToken/properties/DescriptionProperty";

import { AppFooter } from "../../../layout/Footer";
import { AppHeader } from "../../../layout/Header";

import { ImportedEntityType } from "./modals/DeleteImport";
import { useGetManageImportTokenQuery } from "./queries.graphql.generated";

export const ManageImportedToken = () => {
  const { trackEvent, EventType } = useAnalytics();
  const { tokenId } = useParams();

  const { data: tokenData, loading: tokenLoading } =
    useGetManageImportTokenQuery({
      variables: {
        tokenId: tokenId ?? "",
      },
      fetchPolicy: "network-only",
    });

  const token = tokenData?.getImportedToken;

  useEffect(() => {
    trackEvent(EventType.EditToken_Viewed, { tokenId });
  }, []);

  const handleDeleteOnClick = useCallback(() => {
    updateModalVar({
      showModal: ModalType.DELETE_IMPORT,
      data: {
        id: tokenId,
        importedEntityType: token?.tokenId
          ? ImportedEntityType.TOKEN
          : ImportedEntityType.COLLECTION,
      },
    });
  }, [tokenId]);

  if (tokenLoading) {
    return <LoadingPage />;
  }

  if (!token) {
    return <NotFound redirectUrl={TOOLS_URL.home} />;
  }

  const blockchainEntity = mapImportTokenToBlockchainEntity(token);

  return (
    <>
      <AppSEO title={blockchainEntity.details.name} />
      <AppShell
        header={
          <AppHeader
            showBrand={false}
            leftButtons={
              <Link to="/">
                <Button
                  color={SECONDARY_COLOR}
                  leftIcon={<ArrowLeftIcon height={12} />}
                >
                  Back
                </Button>
              </Link>
            }
          />
        }
        footer={<AppFooter />}
      >
        <Container size="md">
          <Stack spacing={40}>
            <ManageTokenHeader blockchainEntity={blockchainEntity} />

            <ImportedCard />

            <ContractAddressProperty
              label="Contract address"
              address={token.address}
            />
            <TokenMetadataProperty label="Metadata" value="Imported" />
            {token.importData?.description && (
              <DescriptionProperty
                description={token.importData?.description}
              />
            )}
            <Button
              fullWidth
              size="xl"
              onClick={handleDeleteOnClick}
              loading={tokenLoading}
            >
              Remove {token.tokenId ? "token" : "collection"}
            </Button>
          </Stack>
        </Container>
      </AppShell>
    </>
  );
};

const ImportedCard = () => {
  const navigate = useNavigate();

  const handleCreateGateClick = () => {
    navigate(TOOLS_URL.gates.create);
  };

  return (
    <Card bg="secondaryBackground.0">
      <Group noWrap position="apart">
        <Group>
          <DownloadFill height={20} width={22} />
          <Stack spacing={0}>
            <Text size="sm" weight={500}>
              Imported token
            </Text>
            <Text size="xs" color={TEXT_COLOR.SECONDARY}>
              You can&apos;t sell this token on Highlight, but you can use it to
              gate mints or custom pages.
            </Text>
          </Stack>
        </Group>
        <Button size="xs" color={OUTLINE_COLOR} onClick={handleCreateGateClick}>
          Create a gate
        </Button>
      </Group>
    </Card>
  );
};

const ContractAddressProperty = ({
  label,
  address,
}: {
  label: string;
  address: string;
}) => (
  <Stack spacing={8}>
    <Text size="lg" weight={500}>
      {label}
    </Text>
    <Card>
      <Group position="apart" noWrap>
        <Group spacing={12} align="center">
          <BracketsEllipsisIcon />
          <Text size="sm">{address}</Text>
        </Group>
        <CopyButton value={address}>
          {({ copied, copy }) => (
            <Anchor onClick={copy} underline={false} fw={500} size="sm">
              {copied ? "Copied!" : "Copy"}
            </Anchor>
          )}
        </CopyButton>
      </Group>
    </Card>
  </Stack>
);

const TokenMetadataProperty = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Stack spacing={8}>
    <Text size="lg" weight={500}>
      {label}
    </Text>
    <Card>
      <Group position="apart">
        <Group spacing={12}>
          <CheckCircle />
          <Text size="sm">{value}</Text>
        </Group>
        <LockFill
          css={css`
            flex-shrink: 0;
            vertical-align: baseline;
          `}
        />
      </Group>
    </Card>
  </Stack>
);
